import { createRootRoute, Link, Outlet, useNavigate, useRouterState } from '@tanstack/react-router'
import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react'

import { SHARE_PATH } from '@gymcloud/nutrition/share'

import { useAuth } from '~/auth/auth-context'
import { SignInScreen } from '~/auth/sign-in-screen'
import { AccountAvatar } from '~/components/account-avatar'
import { UpgradePage } from '~/components/upgrade-page'
import { AccountPanels } from '~/components/account-panels'
import { FloatOut } from '~/components/float-out'
import { FoodHeaderSearch } from '~/components/food-header-search'
import { WeighInNudge } from '~/components/weighin-nudge'
import { ExercisesDialog } from '~/components/exercises-dialog'
import { DiarySteps } from '~/components/diary-steps'
import { SelectionBar } from '~/components/selection-bar'
import { useSelection } from '~/components/selection-context'
import { SelectionProvider } from '~/components/selection-provider'
import { ToastProvider } from '~/components/toaster'
import { WorkoutsDialog } from '~/components/workouts-dialog'
import { GoalReachedScreen } from '~/components/goal-reached-screen'
import { WeeklyCheckinScreen } from '~/components/weekly-checkin-screen'
import { useWeeklyCheckinAsked, weeklyCheckinAnswered } from '~/lib/weekly-checkin-ask'
import {
  BarbellIcon,
  DiaryIcon,
  HelpIcon,
  HistoryIcon,
  LogoMark,
  SearchIcon,
} from '~/components/icons'
import { SearchFilters } from '~/components/search-filters'
import { SearchHelp } from '~/components/search-help'
import { contextHere, searchableOf } from '~/lib/set-search-here'
import { examplesOf } from '@gymcloud/set-search'
import { todayKey } from '~/data/weighins'
import { useWeighIns } from '~/data/weighins-store'
import { useDataState } from '~/data/data-context'
import { currentGoal, weeklyCheckinDue } from '~/data/nutrition'
import { useNutrition } from '~/data/nutrition-store'
import type { ExploreQuery } from '~/data/templates'
import {
  accountPanelOf,
  DAY_DIALOGS,
  dialogDayOf,
  dialogNameOf,
  exploreQueryOf,
  withoutDialog,
  type DialogName,
  type DialogSearch,
} from '~/lib/dialogs'
import { diaryDayKept, onDiaryDay } from '~/lib/diary-day-memory'
import { goalReachedNow, sayGoalReached } from '~/lib/goal-reached'
import { trendOf } from '~/lib/metric-series'
import { loadRecentSearches, saveRecentSearch } from '~/lib/recent-searches'
import { swallowNextClick } from '~/lib/swallow-click'

export const Route = createRootRoute({
  /**
   * Which page is open over the page beneath. It is validated here rather than on the pages
   * that open one, so every route in the app inherits the parameter: settings opens over a
   * set, over a workout, over the timeline, without any of them knowing about it.
   */
  validateSearch: (search: Record<string, unknown>): DialogSearch => {
    const name = dialogNameOf(search.dialog)
    const explore = exploreQueryOf(search.explore)
    const day = dialogDayOf(search.day)
    const template =
      typeof search.template === 'string' && search.template !== '' ? search.template : null
    return {
      ...(name === null ? {} : { dialog: name }),
      /*
       * Explore's own filters. They are kept while a template's page is open as well, because
       * that page was reached from Explore and its way back has to put the filters back.
       */
      ...((name === 'explore' || name === 'template') && explore !== null ? { explore } : {}),
      // The day a weigh-in panel is on, which only those panels carry.
      ...(name !== null && (DAY_DIALOGS as readonly DialogName[]).includes(name) && day !== null
        ? { day }
        : {}),
      // Which template a template's page is about, which only that page carries.
      ...(name === 'template' && template !== null ? { template } : {}),
      // The way the goal process opens on, where the import sent it there.
      ...(search.way === 'coached' ? { way: 'coached' as const } : {}),
      // The aim it opens on, where reaching a goal sent it there to aim the other way.
      ...(search.aim === 'lose' || search.aim === 'gain' ? { aim: search.aim } : {}),
    }
  },
  component: RootLayout,
  notFoundComponent: NotFound,
})

function RootLayout() {
  const { session, plan } = useAuth()
  const navigate = useNavigate()
  const { dialog, explore, day, template, measure } = Route.useSearch()
  const pathname = useRouterState({ select: (routerState) => routerState.location.pathname })
  /*
   * The link's payload, read off the address rather than off this route's own search: `d`
   * belongs to `/shared` and is no part of what the root validates, and the root has to say
   * something about it before that route is ever reached.
   */
  const searchStr = useRouterState({ select: (routerState) => routerState.location.searchStr })

  /*
   * Nothing else in the app is reachable until signed in — but a link arriving says what it
   * carries in brief, and asks for sign-in for the rest of it: the nutrition spec, Opening a
   * link. A link is how somebody first meets this app, and a wall with nothing behind it is a
   * link that says nothing at all.
   */
  if (!session) {
    const carried = new URLSearchParams(searchStr).get('d')
    const arriving = pathname === SHARE_PATH && carried !== null ? carried : undefined
    return <SignInScreen taste={arriving} />
  }

  /*
   * And nothing else is reachable on an account whose plan keeps nothing in the cloud. This
   * app reads what the server holds; for that account the server holds nothing, so every page
   * behind this would be an empty one with no explanation on it.
   */
  if (plan !== null && plan.storageBytes === 0) {
    return <UpgradePage />
  }

  // The template editor is a workspace rather than a page: it wants the whole width. It is the
  // address under a template's own that this matches — the page itself is a dialog over
  // whatever it was opened from, and the router is never resting on its address at all.
  const workspace = /^\/templates\/[^/]+\/edit$/.test(pathname)
  // A set fills the window, and the header — which never unmounts — is under it. Anchored the
  // way the line above is: a set's own page is full screen, the editor under it is not.
  const fullScreen = /^\/sets\/[^/]+$/.test(pathname)

  // Replaced rather than pushed: opening the dialog was the history entry, so the
  // way back out of it is already there and closing must not lay another on top.
  const closeDialog = () => {
    void navigate({
      to: '.',
      search: ({ dialog: _closed, explore: _shown, template: _about, ...rest }) => rest,
      replace: true,
      resetScroll: false,
      // The dialog plays its own leave; a root snapshot pass adds nothing over a
      // page that never moved.
      viewTransition: false,
    })
  }

  return (
    <ToastProvider>
      <SelectionProvider>
        {/* Clipped sideways so a full-bleed hero's 100vw cannot leak a horizontal scrollbar.
          Isolated so a page can hang a backdrop at negative z — an ambient glow — and have
          it land on this surface instead of vanishing behind it. */}
        <div className="isolate min-h-dvh overflow-x-clip bg-surface text-ink">
          <AppHeader nudgeable={!fullScreen} />
          <main className={workspace ? 'pt-16' : 'mx-auto max-w-7xl px-4 pt-20 pb-12 sm:px-6'}>
            <Outlet />
          </main>
          {dialog === undefined ? null : (
            <AppDialog
              name={dialog}
              day={day ?? null}
              measure={measure ?? null}
              explore={explore ?? {}}
              template={template ?? null}
              onClose={closeDialog}
            />
          )}
          <GoalReached />
          <WeeklyCheckin />
        </div>
      </SelectionProvider>
    </ToastProvider>
  )
}

/**
 * Reaching a body goal, raised the moment the weigh-in that did it lands — see the nutrition
 * spec, Reaching it. It stands over whatever page the weigh-in was logged from, which is why it
 * lives here rather than on the panel that saved it: on web a weigh-in is logged from a card
 * that can be opened over any page in the app.
 *
 * Only the newest weigh-in can raise it. A crossing further back is history rather than news,
 * and a page load months later must not congratulate somebody for a morning in March.
 */
function GoalReached() {
  const data = useNutrition()
  const weighIns = useWeighIns()
  const [past, setPast] = useState<string | null>(null)
  if (data === null || weighIns === null) {
    return null
  }
  const goal = currentGoal(data.goals)
  const scale = [...weighIns]
    .filter((weighIn) => weighIn.weightKg !== null)
    .sort((a, b) => a.day.localeCompare(b.day))
    .map((weighIn) => ({ day: weighIn.day, value: weighIn.weightKg ?? 0 }))
  const trend = trendOf(scale)
  const reading = goalReachedNow(goal, scale, trend)
  if (goal === null || reading === null || past === goal.id + reading) {
    return null
  }
  return (
    <GoalReachedScreen
      goal={goal}
      reading={reading}
      scale={scale}
      trend={trend}
      data={data}
      weighIns={weighIns}
      onClose={() => {
        // Said, whichever way it was left: the ways on are a choice, not a second chance.
        sayGoalReached(goal.id, reading)
        setPast(goal.id + reading)
      }}
    />
  )
}

/**
 * The weekly check-in, raised on the first opening once a weigh-in carrying a weight has been made
 * on the check-in's weekday or after, and kept opening until it is answered — see the nutrition
 * spec. Waited on rather than assumed: it opens only once the diary and the weigh-ins have arrived
 * — the check-in names the expenditure, which needs both, and it is the weigh-ins that say whether
 * it is due at all.
 */
function WeeklyCheckin() {
  const data = useNutrition()
  const weighIns = useWeighIns()
  const asked = useWeeklyCheckinAsked()
  const [put, setPut] = useState<string | null>(null)
  const today = todayKey()
  /*
   * Asked for from the account, it is raised whether or not its day has come and whether or
   * not a weigh-in is in — and it is raised again even on a day it has already been put down
   * on, the asking being somebody saying they want it now.
   */
  if (data === null || weighIns === null) {
    return null
  }
  if (!asked && (!weeklyCheckinDue(data, today, weighIns) || put === today)) {
    return null
  }
  return (
    <WeeklyCheckinScreen
      data={data}
      weighIns={weighIns}
      onClose={() => {
        setPut(today)
        weeklyCheckinAnswered()
      }}
    />
  )
}

/** Whichever page the URL says is open over this one. */
function AppDialog({
  name,
  day,
  measure,
  explore,
  template,
  onClose,
}: {
  name: DialogName
  /** The day a weigh-in panel is on — see `AccountPanels`. */
  day: string | null
  /** Which reading a weigh-in panel opens on — see `AccountPanels`. */
  measure: string | null
  /** What Explore is showing, which its own panel and a template's page both read. */
  explore: ExploreQuery
  /** Which template the template panel is about. */
  template: string | null
  onClose: () => void
}) {
  switch (name) {
    case 'workouts':
      return <WorkoutsDialog onClose={onClose} />
    case 'exercises':
      return <ExercisesDialog onClose={onClose} />
    default: {
      /*
       * A name nothing has been built for yet draws nothing, rather than falling through to
       * something that was not asked for.
       */
      return accountPanelOf(name) === null ? null : (
        <AccountPanels
          name={name}
          day={day}
          measure={measure}
          explore={explore}
          template={template}
          onClose={onClose}
        />
      )
    }
  }
}

/** The Google Photos arrangement: the mark, search in the middle, the account on the right. */
function AppHeader({
  nudgeable,
}: {
  /** Whether the page under the account has room for the nudge — see `WeighInNudge`. */
  nudgeable: boolean
}) {
  const { selection } = useSelection()
  const { session } = useAuth()
  // The account is named after whoever is signed in, wherever it is drawn.
  const you = session?.user.name ?? 'You'
  // You and the pages under it are the account's own, and the account says so while one of
  // them is open — worked out here, since the router's idea of an active link would still
  // call it active with a dialog standing over the page.
  const onYou = useRouterState({
    select: (routerState) => routerState.location.pathname.startsWith('/you'),
  })
  const searching = useRouterState({
    select: (routerState) => searchOn(routerState.location.pathname),
  })
  // Undoing is the diary's, so its controls stand in the header only while the diary is open.
  const onDiary = useRouterState({
    select: (routerState) => routerState.location.pathname.startsWith('/diary'),
  })
  // At the very top the header lies flat — no surface, no border — so it rests on whatever
  // the page opens with, an exercise hero included. The first scrolled pixel raises it:
  // surface, hairline and shadow ease in together, and settle back out at the top.
  const [raised, setRaised] = useState(false)
  useEffect(() => {
    const onScroll = () => {
      setRaised(window.scrollY > 0)
    }
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      window.removeEventListener('scroll', onScroll)
    }
  }, [])

  /*
   * While something is selected the header is the selection's — it stands in the same row and
   * hands the page back when it closes, so nothing under it moves either way. The bar is the
   * whole of it: the search and the account are about going somewhere else, and going
   * somewhere else is what closing the selection is for.
   */
  if (selection !== null) {
    return (
      <header
        className={`fixed inset-x-0 top-0 z-30 flex h-16 items-center gap-3 border-b px-4 sm:gap-6 sm:px-6 ${
          raised
            ? 'border-white/5 bg-surface/95 shadow-lg shadow-black/30 backdrop-blur'
            : 'border-transparent bg-surface'
        }`}
      >
        <SelectionBar selection={selection} />
      </header>
    )
  }

  /*
   * Three columns from `sm` up, the outer two the same width, so the search stands in the
   * middle of the window rather than in the middle of what is left beside the navigation.
   */
  return (
    <header
      className={`fixed inset-x-0 top-0 z-30 flex h-16 items-center gap-2 border-b px-3 transition-[background-color,border-color,box-shadow,backdrop-filter] duration-300 sm:grid sm:grid-cols-[1fr_minmax(0,42rem)_1fr] sm:gap-6 sm:px-6 ${
        raised
          ? 'border-white/5 bg-surface/95 shadow-lg shadow-black/30 backdrop-blur'
          : 'border-transparent bg-transparent shadow-none backdrop-blur-0'
      }`}
    >
      <div className="flex min-w-0 shrink-0 items-center gap-2 sm:gap-6">
        <Link
          to="/"
          aria-label="MixedGrip"
          // Squared off rather than rounded, because the mark is wider than it is tall and a
          // stadium around it reads as a squeeze. Otherwise it is spaced like every other control
          // in the bar: a 28 mark with 10 around it. The negative margin matches that padding, so
          // the mark's ink still lines up with the page gutter.
          className="-ml-2.5 flex shrink-0 items-center rounded-xl p-2.5 transition-colors hover:bg-hover"
        >
          <LogoMark className="h-7 w-auto" />
        </Link>

        <MainNav />
      </div>

      {/*
        The field stands on the timeline, on the results of a search, and on the food log and
        every page under it — the app spec, Getting around — and says what it searches. A page
        without one leaves the middle of the bar empty rather than closing the bar up, so the
        navigation and the account stand where they stand on every page.

        It arrives and leaves the way every other floating thing does rather than being there
        or not between two frames: the middle keeps its box, and the field eases into it and
        out of it as the page under the header changes.
      */}
      <div className="relative h-12 min-w-0 flex-1 sm:w-full">
        <FloatOut open={searching === 'training'}>
          <HeaderSearch />
        </FloatOut>
        <FloatOut open={searching === 'food'}>
          <FoodHeaderSearch />
        </FloatOut>
      </div>

      <div className="flex shrink-0 items-center justify-end gap-1">
        {/*
          Undoing belongs to the diary, so it stands here only while the diary is open — in the
          place a selection's own controls take, since both are about what can be done to what
          is on the page rather than about going somewhere else.
        */}
        {onDiary && <DiarySteps />}
        {/*
          The account is a way to You and nothing else — the app spec, Getting around. What
          used to hang off it in a menu stands on You, which is the page about the account, and
          a menu offering the same rows a page offers is that page said twice.
        */}
        <Link
          to="/you"
          search={withoutDialog}
          aria-label={you}
          aria-current={onYou ? 'page' : undefined}
          activeOptions={{ exact: true }}
          /*
           * The same hover as the round header buttons, worn as a halo rather than a fill —
           * and worn for good while You is the page, the way a destination in the navigation
           * is lit while it is open. It is one of the destinations; it simply stands here.
           */
          className={`ml-1 flex rounded-full ring-hover transition-all hover:ring-4 focus-visible:ring-4 focus-visible:ring-white/30 ${
            onYou ? 'ring-4' : 'ring-0'
          }`}
        >
          <AccountAvatar size={40} />
        </Link>
        <WeighInNudge nudgeable={nudgeable} />
      </div>
    </header>
  )
}

/**
 * What the field on a page searches, or null on a page that carries no field at all — the app
 * spec, Getting around. The food log and the pages under it search food; the timeline and the
 * results of a search search training history; everywhere else the middle of the bar is empty.
 */
function searchOn(pathname: string): 'food' | 'training' | null {
  if (pathname.startsWith('/diary')) {
    return 'food'
  }
  return pathname === '/' || pathname === '/search' ? 'training' : null
}

/**
 * The main navigation, per Getting around in the app spec: the timeline and the diary, saying
 * which is open. You is not one of them — the account in the header is the way to it, and
 * Explore is reached from there, as it is on the phone.
 */
function MainNav() {
  const pathname = useRouterState({ select: (routerState) => routerState.location.pathname })
  /*
   * The diary, come back to, stands on the day it was left on — the nutrition spec, The
   * diary — so the way to it names that day, and today where that is the day it was left on.
   */
  const diaryDay = useSyncExternalStore(onDiaryDay, () => diaryDayKept(todayKey()))
  const onDiary = pathname.startsWith('/diary')
  // The results of a search of training history are the timeline's, as its field is.
  const onTimeline = searchOn(pathname) === 'training'
  /*
   * Labelled above `sm`; below it there is no room beside the search and the account for
   * two words, so each destination is its mark alone, named for anything reading the page.
   */
  const item = (current: boolean) =>
    `flex shrink-0 items-center justify-center rounded-full transition-colors sm:px-3 sm:py-1.5 sm:text-sm size-10 sm:size-auto ${
      current ? 'bg-hover font-medium text-ink' : 'text-ink-muted hover:bg-hover hover:text-ink'
    }`
  const mark = 'size-6 sm:hidden'
  const word = 'hidden sm:inline'

  return (
    <nav aria-label="Main" className="flex shrink-0 items-center gap-1">
      <Link
        to="/"
        search={withoutDialog}
        aria-label="Training"
        aria-current={onTimeline ? 'page' : undefined}
        /*
         * Which one is open is worked out here and nowhere else. The router's own idea of an
         * active link is the route it has matched, which is still the timeline while Explore
         * stands over it — and it writes `aria-current` itself, so left alone it would mark
         * two of the three as the page. Read exactly, a link that drops the dialog is not the
         * address a dialog is open at, and the two answers agree again.
         */
        activeOptions={{ exact: true }}
        className={item(onTimeline)}
      >
        <BarbellIcon aria-hidden className={mark} />
        <span className={word}>Training</span>
      </Link>
      <Link
        to="/diary"
        search={(prev) => ({
          ...withoutDialog(prev),
          ...(diaryDay === null ? {} : { day: diaryDay }),
        })}
        aria-label="Food log"
        aria-current={onDiary ? 'page' : undefined}
        activeOptions={{ exact: true }}
        className={item(onDiary)}
      >
        <DiaryIcon aria-hidden className={mark} />
        <span className={word}>Food log</span>
      </Link>
    </nav>
  )
}

/**
 * The search pill. Focused, it opens a panel with the filters and recent searches;
 * submitting lands on the search page, which reads everything from the URL.
 */
function HeaderSearch() {
  const navigate = useNavigate()
  const state = useDataState()
  const location = useRouterState({ select: (routerState) => routerState.location })

  const onSearchPage = location.pathname === '/search'
  const urlSearch = location.search as { q?: string }

  const [term, setTerm] = useState('')
  const [open, setOpen] = useState(false)
  const [recent, setRecent] = useState<string[]>(() => loadRecentSearches())
  /** Whether the panel is saying how search works, in place of the filters and recent searches. */
  const [explaining, setExplaining] = useState(false)
  const { units, distanceUnits } = useAuth()
  // Worked out only while it is being read: it walks the whole history.
  const examples = useMemo(() => {
    if (!explaining) return null
    const workouts = new Map(
      [...state.workouts, ...(state.currentWorkout === null ? [] : [state.currentWorkout])].map(
        (workout) => [workout.id, workout],
      ),
    )
    const runs = new Map(state.enrollments.map((run) => [run.id, run]))
    return examplesOf(
      state.sets.filter((set) => !set.isWarmup).map((set) => searchableOf(set, workouts, runs)),
      contextHere(units, distanceUnits),
    )
  }, [
    explaining,
    state.sets,
    state.workouts,
    state.currentWorkout,
    state.enrollments,
    units,
    distanceUnits,
  ])
  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLFormElement>(null)

  // Closing on blur would beat the click to any control inside the panel — a press on
  // a filter must land. So the panel closes on a pointer that goes down outside it, and a
  // filter's own popover, floating over the page rather than inside the panel, is not outside.
  useEffect(() => {
    if (!open) {
      return
    }
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node
      if (target instanceof Element && target.closest('[data-slot="popover-content"]')) {
        return
      }
      if (containerRef.current && !containerRef.current.contains(target)) {
        // Putting the panel away is the whole of what the press does — the click that follows
        // it would otherwise press whatever the panel was standing over.
        swallowNextClick()
        setOpen(false)
        setExplaining(false)
      }
    }
    document.addEventListener('pointerdown', onPointerDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
    }
  }, [open])

  // The URL owns the search; the field follows it, so deep links and back both read right.
  // Adjusted during render rather than in an effect — no cascading render.
  const urlKey = onSearchPage ? (urlSearch.q ?? '') : null
  const [syncedUrlKey, setSyncedUrlKey] = useState<string | null>(null)
  if (urlKey !== null && urlKey !== syncedUrlKey) {
    setSyncedUrlKey(urlKey)
    setTerm(urlSearch.q ?? '')
  }

  const runSearch = (query: string) => {
    const trimmed = query.trim()
    if (trimmed === '') {
      return
    }
    saveRecentSearch(trimmed)
    setRecent(loadRecentSearches())
    void navigate({
      to: '/search',
      search: { q: trimmed },
    })
  }

  const needle = term.trim().toLowerCase()
  const suggestions =
    needle === '' ? recent : recent.filter((candidate) => candidate.toLowerCase().includes(needle))

  return (
    <form
      ref={containerRef}
      role="search"
      className="absolute inset-x-0 top-0 h-12"
      onFocus={() => {
        setOpen(true)
      }}
      onSubmit={(event) => {
        event.preventDefault()
        runSearch(term)
        inputRef.current?.blur()
        setOpen(false)
      }}
    >
      {/*
        Open, the field and its tray are one surface: the field at the top of a single
        rounded card, a rule under it, then the filters and the recent searches. The card
        floats over the page from the form's own box, so the header does not grow.
      */}
      <div
        className={`absolute inset-x-0 top-0 overflow-hidden transition-colors ${
          open
            ? 'rounded-3xl bg-surface-overlay shadow-2xl ring-1 ring-white/10'
            : 'rounded-full bg-surface-raised'
        }`}
      >
        <div className="flex h-12 items-center gap-2 px-3 sm:gap-3 sm:px-4">
          <SearchIcon className="h-6 w-6 shrink-0 text-ink-muted" />
          <input
            ref={inputRef}
            type="search"
            aria-label="Search training history"
            placeholder="Search training history"
            value={term}
            onChange={(event) => {
              setTerm(event.target.value)
            }}
            onKeyDown={(event) => {
              if (event.key === 'Escape') {
                setOpen(false)
                inputRef.current?.blur()
              }
            }}
            // Full height, or the round clear button gets clipped by the text box.
            className="h-full w-full bg-transparent text-lg outline-none placeholder:text-ink-muted"
          />
          {/* How search works, said in the panel in place of what it otherwise holds. */}
          <button
            type="button"
            aria-label="How search works"
            aria-pressed={explaining}
            className={`shrink-0 rounded-full p-1.5 transition-colors hover:bg-hover ${
              explaining ? 'text-accent' : 'text-ink-muted hover:text-ink'
            }`}
            onClick={() => {
              setOpen(true)
              setExplaining((held) => !held)
            }}
          >
            <HelpIcon aria-hidden className="size-6" />
          </button>
          {/* Enter must submit even with the filter fields present — the form needs a
            submit button for implicit submission to exist. */}
          <button type="submit" hidden tabIndex={-1} aria-hidden="true">
            Search
          </button>
        </div>

        {open && explaining && examples !== null && (
          <div className="max-h-[calc(100dvh-8rem)] overflow-y-auto border-t border-white/10 p-3">
            <SearchHelp
              examples={examples}
              onSearch={(example) => {
                setTerm(example)
                runSearch(example)
                setExplaining(false)
                setOpen(false)
              }}
            />
          </div>
        )}

        {open && !explaining && (
          <div className="border-t border-white/10 p-3">
            {/*
              Each filter writes into the field, and searches with what it wrote. A filter's
              change is not a search somebody submitted, so it is not remembered as a recent one,
              and on the results it stands in place of the search it changed.
            */}
            <SearchFilters
              search={term}
              onSearch={(next) => {
                setTerm(next)
                const trimmed = next.trim()
                if (trimmed === '' && !onSearchPage) return
                void navigate({
                  to: '/search',
                  search: trimmed === '' ? {} : { q: trimmed },
                  replace: onSearchPage,
                })
              }}
            />

            {suggestions.length > 0 && (
              <div className="mt-3 border-t border-white/5 pt-2">
                {suggestions.map((candidate) => (
                  <button
                    key={candidate}
                    type="button"
                    className="menu-row"
                    onClick={() => {
                      setTerm(candidate)
                      runSearch(candidate)
                      setOpen(false)
                    }}
                  >
                    <HistoryIcon className="h-6 w-6 shrink-0 text-ink-muted" />
                    <span className="truncate">{candidate}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </form>
  )
}

function NotFound() {
  return (
    <section className="mx-auto max-w-3xl space-y-4 px-6 py-16">
      <h1 className="text-2xl font-medium">Not found</h1>
      <p className="text-ink-muted">There is nothing at this address.</p>
      <Link to="/" className="text-accent underline">
        Go home
      </Link>
    </section>
  )
}
